'use client'

import { useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'

type ForumReport = {
  id: number
  post_id: number
  reason: string
  details: string
  created_at: string
  reporter_handle: string
  post_author: string
  post_body: string
  topic_id: number
  topic_title: string
}

function formatDate(value: string) {
  const date = new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`)
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function AdminForumReports() {
  const [authorized, setAuthorized] = useState<boolean | null>(null)
  const [reports, setReports] = useState<ForumReport[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  async function loadReports() {
    const response = await fetch('/api/admin/forum/reports')
    if (!response.ok) {
      setAuthorized(false)
      return
    }
    const data = (await response.json()) as { reports?: ForumReport[] }
    setAuthorized(true)
    setReports(data.reports || [])
  }

  useEffect(() => {
    loadReports()
      .catch(() => setAuthorized(false))
      .finally(() => setLoading(false))
  }, [])

  async function resolve(report: ForumReport) {
    setBusyId(report.id)
    setError('')
    setNotice('')
    try {
      const response = await fetch(`/api/admin/forum/reports/${report.id}/resolve`, {
        method: 'POST',
      })
      const data = (await response.json().catch(() => ({}))) as { error?: string }
      if (!response.ok) throw new Error(data.error || 'Could not resolve the report.')
      setNotice('Report marked resolved.')
      await loadReports()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resolve the report.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl">
      <section className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-primary">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-lg font-semibold">Forum moderation queue</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Member reports are separate from bug reports. Review the context, open the post, and
              resolve it after taking any needed action.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : authorized === false ? (
          <p className="mt-6 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Administrator access is required.
          </p>
        ) : reports.length === 0 ? (
          <p className="mt-6 rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            There are no open forum reports.
          </p>
        ) : (
          <div className="mt-5 flex flex-col gap-3">
            {reports.map((report) => (
              <article key={report.id} className="rounded-xl border border-border bg-background p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="flex items-center gap-2 font-medium">
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                      {report.reason.replace('-', ' ')}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Reported by {report.reporter_handle} · {formatDate(report.created_at)}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    nativeButton={false}
                    render={
                      <a
                        href={`/forum/topic/${report.topic_id}#post-${report.post_id}`}
                        target="_blank"
                        rel="noreferrer"
                      />
                    }
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Open post
                  </Button>
                </div>
                <p className="mt-3 text-sm font-medium">{report.topic_title}</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                  {report.post_body}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  Post author: {report.post_author}
                  {report.details ? ` · Reporter details: ${report.details}` : ''}
                </p>
                <div className="mt-3 flex justify-end">
                  <Button
                    size="sm"
                    disabled={busyId === report.id}
                    onClick={() => resolve(report)}
                  >
                    {busyId === report.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" />
                    )}
                    Mark resolved
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}

        {error && (
          <p role="alert" className="mt-3 text-sm text-destructive">
            {error}
          </p>
        )}
        {notice && (
          <p role="status" className="mt-3 text-sm text-primary">
            {notice}
          </p>
        )}
      </section>
    </div>
  )
}
